import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { WithholdTaxSummaryEditService } from './withhold-tax-summary-edit.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  AppkitDropdownItem,
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  handleAppkitValueSelect,
  mapToAppkitDropdownItems,
  syncAppkitDropdownSelection
} from '../../../shared/utils/appkit-dropdown.util';
import { setFormDropdownAriaLabel } from '../../shared/ftc-form-dropdown.support';

@Component({
    selector: 'gtw-withhold-tax-summary-edit',
    templateUrl: './withhold-tax-summary-edit.component.html',
    styleUrls: ['./withhold-tax-summary-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WithholdTaxSummaryEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Flags to prevent duplicate initialization (V9 pattern)
  private isInitialized = false;
  private isLoading = false;

  private readonly subs = new Subscription();

  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};
  gridData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  isSaveCredClicked = false;
  crudLoading = false;
  showFormLoader = true;
  modalTitle = "Withholding Tax for LEID";

  // Form data - matching AngularJS vm.formData
  formData: any = {
    panel: true,                    // true = Creditable Tax, false = TB Amount Not Creditable
    tabular: true,                  // Show loading spinners in table
    isSection909SplitrTax: false,   // Show Basket and Country fields
    isSectionReductionTax: false,   // Show Trading Partner, Basket and Country fields
    LEID: '',
    openVariance: '0',
    withHoldTaxTrailBalanceAmt: '0',
    totalWithHoldTaxAmt: '0',
    withHoldVarianceTaxAmt: '0',
    whTaxOnDividend: null,
    selectStdReasonCd: null as string | null,
    applicableYearKey: '',
    nonCrdAmount: '',
    detailReason: '',
    selectedSplitterBasketCatg: '',
    selectedSplitterCountryDesc: '',
    selectedSplitterCountryKey: '',
    selectedSplitterTradingPartnerCode: '',
    editFlagSet: false,
    tagKey: null,
    ReturnAsFiled: false,
    filterTaxYear: '',
    scenario: ''
  };

  // Dropdown data - matching AngularJS variables
  attribMastData: any[] = [];
  basketCatgData: any[] = [];
  countryData: any[] = [];
  taxYearData: any[] = [];
  stdReasonCodeData: any[] = [];
  basketCodeDefault: any[] = [];
  countryUS: any[] = [];
  taxPriorYearStdReason: any[] = [];
  section909SplitrTax: any[] = [];
  sectionReduction_G: any[] = [];
  sectionReduction_A: any[] = [];
  filteredTaxYearData: any[] = [];
  tradingPartnerCodeData: any[] = [];
  beatYesforeignTradingPartnerData: any[] = [];
  beatNoforeignTradingPartnerData: any[] = [];
  
  // Key values for comparison
  taxPriorYearCodeKey: string = '';
  section909SplitrTaxKey: string = '';
  sectionReduction_GKEY: string = '';
  sectionReduction_AKEY: string = '';

  // Data arrays
  defaultsMultipleSelected: any[] = [];
  ftcTagDtlsData: any[] = [];
  summaryInfo: any[] = [];
  gridDataMultipleSelect: any = null;
  is_locked: any = null;
  
  // Cached non-creditable records (performance optimization - avoid method calls in template)
  nonCreditableRecords: any[] = [];
  
  // PrimeNG dropdown options
  tradingPartnerOptions: any[] = [];
  stdReasonCodeOptions: any[] = [];
  taxYearOptions: any[] = [];
  basketOptions: any[] = [];
  countryOptions: any[] = [];

  stdReasonCodeOptionsView: AppkitDropdownItem[] = [];
  taxYearOptionsView: AppkitDropdownItem[] = [];
  tradingPartnerOptionsView: AppkitDropdownItem[] = [];
  basketOptionsView: AppkitDropdownItem[] = [];
  countryOptionsView: AppkitDropdownItem[] = [];
  selectedTradingPartnerCodeItem: AppkitDropdownItem | null = null;
  selectedCountryCodeItem: AppkitDropdownItem | null = null;

  setFormDropdownAriaLabel = setFormDropdownAriaLabel;
  
  // Delete confirmation modal
  showDeleteModal = false;
  deleteRowData: any = null;
  deleteModalConfig: ConfirmationModalConfig = {
    title: ' Delete Tax Amount',
    message: 'Are you sure you want to remove this Tax Amount?',
    iconClass: 'fa fa-trash',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger'
  };
  
  // User settings
  logged_in_user: string = '';
  jcd_key: number = 250;

  // Selected values for custom selects
  selectedTradingPartnerCode: string | null = null;
  selectedCountryCode: any = null;
  selectedCountry: string = '';
  selectedTradingPartnerLEID: string = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private jsonObjectFactory: JsonObjectFactoryService,
    private withholdTaxService: WithholdTaxSummaryEditService,
    private http: HttpClient
  ) { }

  ngOnInit() {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen);
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gp = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp);
    this.globalService = { ...header, globalParams: gp };
    this.appService.USER_SETTINGS = header.userSettingsData || {};
    this.appService.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = fp;
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : (row || {});
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = g != null && typeof g === 'object' ? g : {};
  }

  // V9: Gate function to prevent duplicate initialization
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    
    // Check if ALL required data is present before initializing
    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    
    // Check for gridData as well (if available)
    if (!this.gridData || Object.keys(this.gridData).length === 0) {
      return;
    }
    
    this.isInitialized = true;
    this.init();
  }

  init() {
    try {
      // Get filter params
      this.formData.filterTaxYear = this.filterParams.tax_year || this.globalService?.globalParams?.tax_year;
      this.formData.scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario;
      this.jcd_key = this.globalService?.globalParams?.jcd_key || 250;
      
      // Get logged in user
      this.logged_in_user = this.appService.USER_SETTINGS?.user?.sso_id || '';

      // Process gridData if available (matching AngularJS lines 350-372)
      this.processGridData();

      // Load dropdown data
      this.getTradingPartnerData();
      this.getAttribMast();
      this.getRequiredDropDowns();
      
      // Check lock status
      this.checkLockStatus();
      
      // Get summary info
      if (this.rowData) {
        this.getSummaryInformation();
      }

      this.showFormLoader = false;
      this.cdr.markForCheck();
    } catch (e) {
      console.error('Error in withhold-tax-summary-edit init:', e);
      this.showFormLoader = false;
      this.cdr.markForCheck();
    }
  }

  processGridData() {
    // Matching AngularJS lines 350-372
    if (this.gridData && this.gridData.rowData) {
      this.gridDataMultipleSelect = _.clone(this.gridData);
      
      const rowData = this.gridDataMultipleSelect.rowData;
      
      // Set form data from rowData
      this.formData.LEID = rowData.LEID;
      this.formData.openVariance = this.formatNumber(rowData.VARIANCE_AMT);
      this.formData.withHoldTaxTrailBalanceAmt = this.formatNumber(rowData.TB_AMT);
      this.formData.totalWithHoldTaxAmt = this.formatNumber(rowData.SOURCED_AMT);
      this.formData.withHoldVarianceTaxAmt = this.formatNumber(rowData.VARIANCE_AMT);
      this.formData.whTaxOnDividend = rowData.WHT_DIVIDEND ? this.numberWithCommas(parseFloat(rowData.WHT_DIVIDEND)) : null;

      // Get tag details data
      if (this.GENERAL_CONFIG?.base_url && rowData.COMBINATION_KEY) {
        this.getTagDetailsData(rowData.COMBINATION_KEY);
      }
      
      // Push rowData to defaultsMultipleSelected initially
      this.defaultsMultipleSelected.push(rowData);
      this.updateNonCreditableRecords(); // Update cached records
      this.formData.tabular = false;
      
      // Update modal title
      this.modalTitle = `Withholding Tax for LEID - ${this.formData.LEID}`;
    }
  }

  formatNumber(value: any): string {
    if (value == null || value == undefined) return '0';
    const num = parseInt(('' + value).replace(/,/g, ''));
    return isNaN(num) ? '0' : num.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }

  numberWithCommas(x: any): string {
    if (x == null || x == undefined) return '';
    return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
  }

  // Matching AngularJS vm.whTaxOnDividendAmount
  whTaxOnDividendAmount(event: any) {
    if (this.formData.whTaxOnDividend) {
      this.formData.whTaxOnDividend = this.formData.whTaxOnDividend.replace(/(?!^-)[^0-9\.]/g, '');
      let amount = this.formData.whTaxOnDividend;
      if (amount.indexOf('.') !== -1) {
        let amountSp = amount.split('.');
        let bPre = amountSp[0];
        let aPre = amountSp[1];
        if (bPre && bPre.length > 20) {
          bPre = bPre.substring(0, 19);
        }
        if (aPre && aPre.length > 2) {
          aPre = aPre.substring(0, 2);
        }
        this.formData.whTaxOnDividend = bPre + "." + aPre;
      } else if (amount.length > 20) {
        amount = amount.substring(0, amount.length - 1);
        this.formData.whTaxOnDividend = amount;
      }
    }
    if (this.formData.whTaxOnDividend) {
      this.formData.whTaxOnDividend = this.numberWithCommas(this.formData.whTaxOnDividend);
    }
    this.cdr.markForCheck();
  }

  checkLockStatus() {
    if (!this.GENERAL_CONFIG?.base_url) return;

    // Merge filterParams with local params
    const lockparams = _.merge({}, this.filterParams, {
      action_code: 'n10cpv',
      p_scenario: this.formData.scenario,
      p_jcd_key: this.jcd_key
    });

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      lockparams,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.is_locked = response.jsonObject;
          this.formData.ReturnAsFiled = (this.is_locked != undefined && this.is_locked != null && this.is_locked[0]?.LOCK_STATUS == 'Y');
          this.cdr.markForCheck();
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  // Matching AngularJS vm.getTradingPartnerData
  getTradingPartnerData() {
    if (!this.GENERAL_CONFIG?.base_url) return;

    // Merge filterParams with local params
    const params = _.merge({}, this.filterParams, {
      action_code: 'ayqh3u',
      p_scenario: this.formData.scenario,
      p_jcd_key: this.jcd_key
    });

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.beatYesforeignTradingPartnerData = _.filter(response.jsonObject, (p: any) => _.includes(['RELATED_FGN_BEAT_YES'], p.CODE_GROUP));
          this.beatNoforeignTradingPartnerData = _.filter(response.jsonObject, (p: any) => _.includes(['RELATED_FGN_BEAT_NO'], p.CODE_GROUP));
          this.tradingPartnerCodeData = _.filter(response.jsonObject, (p: any) => _.includes(['RELATED_FGN_BEAT_YES', 'RELATED_FGN_BEAT_NO'], p.CODE_GROUP));
          // Convert to PrimeNG format
          this.tradingPartnerOptions = this.tradingPartnerCodeData.map((tp: any) => ({
            label: tp.LE_NAME,
            value: tp.COMBINATION_KEY
          }));
          this.cdr.markForCheck();
        }
      },
      (error: any) => {
        console.error('Error loading trading partner data:', error);
      }
    );
  }

  // Matching AngularJS vm.getAttribMast
  getAttribMast() {
    if (!this.GENERAL_CONFIG?.base_url) return;

    // Merge filterParams with local params
    const params = _.merge({}, this.filterParams, { action_code: '5u7cuj' });

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.attribMastData = response.jsonObject;
          this.cdr.markForCheck();
        }
      },
      (error: any) => {
        console.error('Error loading attribute master data:', error);
      }
    );
  }

  // Matching AngularJS vm.getRequiredDropDowns
  getRequiredDropDowns() {
    if (!this.GENERAL_CONFIG?.base_url) return;

    // Merge filterParams with local params
    const params = _.merge({}, this.filterParams, {
      action_code: 'e83e1x',
      tax_year: this.formData.filterTaxYear
    });

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.basketCatgData = _.filter(response.jsonObject, (p: any) => _.includes(['SOURCE_CODE'], p.CODE_GROUP));
          this.countryData = _.filter(response.jsonObject, (p: any) => _.includes(['COUNTRY_CODE'], p.CODE_GROUP));
          this.taxYearData = _.filter(response.jsonObject, (p: any) => _.includes(['TAX_YEAR'], p.CODE_GROUP));
          this.stdReasonCodeData = _.filter(response.jsonObject, (p: any) => _.includes(['STD_REASON_CODE'], p.CODE_GROUP));
          
          this.basketCodeDefault = _.filter(this.basketCatgData, (p: any) => _.includes(['BRANCH'], p.CODE_SHORT_DESC));
          this.countryUS = _.filter(this.countryData, (p: any) => _.includes(['US'], p.CODE_SHORT_DESC));
          this.taxPriorYearStdReason = _.filter(this.stdReasonCodeData, (p: any) => _.includes(['TPPY'], p.CODE_SHORT_DESC));
          this.section909SplitrTax = _.filter(this.stdReasonCodeData, (p: any) => _.includes(['SUSPT'], p.CODE_SHORT_DESC));
          this.sectionReduction_G = _.filter(this.stdReasonCodeData, (p: any) => _.includes(['RTX-965(g)'], p.CODE_SHORT_DESC));
          this.sectionReduction_A = _.filter(this.stdReasonCodeData, (p: any) => _.includes(['RTX-245A'], p.CODE_SHORT_DESC));

          if (this.taxPriorYearStdReason.length > 0) {
            this.taxPriorYearCodeKey = '' + this.taxPriorYearStdReason[0].CODE_KEY;
          }
          if (this.section909SplitrTax.length > 0) {
            this.section909SplitrTaxKey = '' + this.section909SplitrTax[0].CODE_KEY;
          }
          if (this.sectionReduction_G.length > 0) {
            this.sectionReduction_GKEY = '' + this.sectionReduction_G[0].CODE_KEY;
          }
          if (this.sectionReduction_A.length > 0) {
            this.sectionReduction_AKEY = '' + this.sectionReduction_A[0].CODE_KEY;
          }

          // Convert to PrimeNG dropdown format (no empty option - placeholder handles null state)
          this.stdReasonCodeOptions = this.stdReasonCodeData.map((item: any) => ({
            label: item.CODE_DISP_DESC,
            value: '' + item.CODE_KEY
          }));
          this.basketOptions = this.basketCatgData.map((item: any) => ({
            label: item.CODE_DISP_DESC,
            value: '' + item.CODE_KEY
          }));
          // No empty option - placeholder handles null state, X button only shows when value is selected
          this.countryOptions = this.countryData.map((item: any) => ({
            label: item.CODE_DISP_DESC,
            value: item.CODE_DISP_DESC  // Country uses CODE_DISP_DESC as value
          }));

          this.refreshFormDropdowns();

          // Initialize tax year filters
          this.tillCurrentTaxYear(this.formData.filterTaxYear, 0, 11);
          this.isTaxPriorYear(); // Use immediate version for programmatic call

          this.cdr.markForCheck();
        }
      },
      (error: any) => {
        console.error('Error loading dropdown data:', error);
      }
    );
  }

  // Matching AngularJS vm.getTagDetailsData
  // Uses JsonObjectFactory.getJSONObj which merges globalParams properly
  getTagDetailsData(cc_key: any) {
    if (!this.GENERAL_CONFIG?.base_url) return;

    // Merge filterParams with local params to ensure all required params are sent
    // (parent_me_string, filing_key, cc_key, etc. from filterParams)
    const params = _.merge({}, this.filterParams, {
      action_code: 'sopgak',
      combination_key: cc_key,
      tax_year: this.formData.filterTaxYear,
      scenario: this.formData.scenario
    });

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.ftcTagDtlsData = response.jsonObject;
          this.processTagDetailsData();
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading tag details data:', error);
      }
    );
  }

  // Process tag details data - matching AngularJS $scope.$watch on ftcTagDtlsData
  processTagDetailsData() {
    if (this.ftcTagDtlsData && this.ftcTagDtlsData.length > 0) {
      const allTagKeys: any[] = [];
      for (const item of this.ftcTagDtlsData) {
        if (!_.includes(allTagKeys, item.TAG_KEY)) {
          allTagKeys.push(item.TAG_KEY);
        }
      }

      if (allTagKeys.length > 0) {
        this.defaultsMultipleSelected = [];
      }

      for (const tagkey of allTagKeys) {
        const newObj: any = _.cloneDeep(this.gridDataMultipleSelect?.rowData || {});
        
        for (const tag of this.ftcTagDtlsData) {
          if (tag.TAG_KEY == tagkey) {
            newObj["TAG_KEY"] = tag.TAG_KEY;
            if (tag.ATTRIB_NAME) {
              const attribName = tag.ATTRIB_NAME.replace(/(^"|"$)/g, '');
              newObj[attribName] = tag.ATTRIB_VALUE;
            }
            newObj["IS_CREDITABLE"] = tag.IS_CREDITABLE;
            newObj.NON_CREDIT_AMT = tag.BRANCH_TAXES_AMT;
          }
        }
        
        this.defaultsMultipleSelected.push(newObj);
      }

      this.updateNonCreditableRecords(); // Update cached records
      this.formData.tabular = false;
      this.cdr.markForCheck();
    }
  }

  // Matching AngularJS: JsonObjectFactory.getJSONObj(GENERAL_CONFIG.base_url+'/loadJsonObject?action_code=35n2lz', params)
  // Uses JsonObjectFactory.getJSONObj which merges globalParams properly
  getSummaryInformation() {
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData) return;

    // Merge filterParams with local params to ensure all required params are sent
    const params = _.merge({}, this.filterParams, {
      action_code: '35n2lz',
      sso_id: this.logged_in_user,
      tax_year: this.formData.filterTaxYear,
      scenario: this.formData.scenario,
      leid: this.rowData.LEID,
      cdr_no: this.rowData.CDR_NO,
      reporting_period: this.rowData.REPORTING_PERIOD,
      me_code: this.rowData.ME_CODE
    });

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess == '1' && response.jsonObject) {
          this.summaryInfo = response.jsonObject;
          this.cdr.markForCheck();
        }
      },
      (error: any) => {
        console.error('Error loading summary information:', error);
      }
    );
  }

  // Matching AngularJS getTaxYearKey
  getTaxYearKey(taxYearDesc: any): string {
    for (const taxYear of this.taxYearData) {
      if (taxYear.CODE_DISP_DESC == taxYearDesc) {
        return '' + taxYear.CODE_KEY;
      }
    }
    return '';
  }

  // Matching AngularJS getCountryKey
  getCountryKey(countryDesc: string): string {
    for (const country of this.countryData) {
      if (country.CODE_DISP_DESC == countryDesc) {
        return '' + country.CODE_KEY;
      }
    }
    return '';
  }

  // Matching AngularJS vm.tillCurrentTaxYear
  // Optimized: Uses filter() instead of manual loop, no unnecessary change detection
  tillCurrentTaxYear(curentYr: number, fromPriorYr: number, tillPriorYr: number, skipChangeDetection: boolean = false) {
    const minYear = curentYr - tillPriorYr;
    const maxYear = curentYr - fromPriorYr;
    
    // Use filter for cleaner code and let Angular's OnPush handle change detection
    this.filteredTaxYearData = this.taxYearData.filter(taxYear => 
      taxYear.CODE_GROUP === 'TAX_YEAR' && 
      taxYear.CODE_DISP_DESC <= maxYear && 
      taxYear.CODE_DISP_DESC >= minYear
    );
    
    // Convert to PrimeNG format
    this.taxYearOptions = this.filteredTaxYearData.map((item: any) => ({
      label: '' + item.CODE_DISP_DESC,
      value: '' + item.CODE_KEY
    }));
    
    // Set default applicable year
    this.formData.applicableYearKey = this.getTaxYearKey(this.formData.filterTaxYear - fromPriorYr);
    this.refreshFormDropdowns();
    
    // Only trigger change detection if not called from processStdReasonChange (which handles it)
    if (!skipChangeDetection) {
      this.cdr.markForCheck();
    }
  }

  // Matching AngularJS vm.isTaxPriorYear
  isTaxPriorYear() {
    this.formData.selectedSplitterCountryDesc = '';
    this.formData.selectedSplitterTradingPartnerCode = '';
    this.selectedTradingPartnerCode = null;
    this.selectedCountryCode = null;

    if (this.formData.selectStdReasonCd && this.formData.selectStdReasonCd == this.taxPriorYearCodeKey) {
      this.tillCurrentTaxYear(this.formData.filterTaxYear, 1, 11, true);
    } else {
      this.tillCurrentTaxYear(this.formData.filterTaxYear, 0, 11, true);
    }

    if (this.formData.selectStdReasonCd && this.formData.selectStdReasonCd == this.section909SplitrTaxKey) {
      this.formData.selectedSplitterBasketCatg = this.basketCodeDefault.length > 0 ? '' + this.basketCodeDefault[0].CODE_KEY : '';
      this.formData.isSection909SplitrTax = true;
    } else {
      this.formData.isSection909SplitrTax = false;
    }

    if (this.formData.selectStdReasonCd && 
        (this.formData.selectStdReasonCd == this.sectionReduction_GKEY || this.formData.selectStdReasonCd == this.sectionReduction_AKEY)) {
      this.formData.selectedSplitterBasketCatg = this.basketCodeDefault.length > 0 ? '' + this.basketCodeDefault[0].CODE_KEY : '';
      this.formData.isSectionReductionTax = true;
    } else {
      this.formData.isSectionReductionTax = false;
    }

    this.cdr.markForCheck();
  }

  // Matching AngularJS vm.changeCountry
  changeCountry(data: any) {
    this.formData.selectedSplitterCountryDesc = this.selectedCountryCode;
    this.cdr.markForCheck();
  }

  // Matching AngularJS vm.changeTradingPartner
  changeTradingPartner(data: any) {
    const exists = _.find(this.tradingPartnerCodeData, ['COMBINATION_KEY', data]);
    if (exists) {
      this.selectedCountry = exists.LE_COUNTRY;
      this.selectedTradingPartnerLEID = exists.COMBINATION_KEY;
    }
    this.formData.selectedSplitterTradingPartnerCode = this.selectedTradingPartnerCode;
    
    const countryExists = _.find(this.countryData, ['CODE_SHORT_DESC', this.selectedCountry]);
    if (countryExists) {
      this.selectedCountryCode = countryExists;
      this.formData.selectedSplitterCountryDesc = countryExists.CODE_KEY;
    } else {
      this.selectedCountryCode = null;
      this.formData.selectedSplitterCountryDesc = '';
    }
    this.cdr.markForCheck();
  }


  // Matching AngularJS vm.redirectUrl
  redirectUrl() {
    // Close modal and redirect to FSI Sourcing screen
    // In Angular, we emit cancel and let AngularJS handle the redirect
    this.requestGridUpdate.emit('redirect_fsi');
  }

  // Matching AngularJS vm.deleteRow - show confirmation modal
  deleteRow(selectedRowData: any) {
    this.deleteRowData = selectedRowData;
    this.showDeleteModal = true;
    this.cdr.markForCheck();
  }

  // Called when user confirms delete in modal
  onDeleteConfirm() {
    this.showDeleteModal = false;
    
    if (!this.deleteRowData) {
      return;
    }

    const selectedRowData = this.deleteRowData;
    const returnClobSettingsObj: any = {
      tax_year: this.formData.filterTaxYear,
      scenario: this.formData.scenario
    };

    const returnObj: any = {
      TAG_KEY: selectedRowData.TAG_KEY,
      COMBINATION_KEY: selectedRowData.COMBINATION_KEY,
      OPERATION_TYPE: 'D',
      GROUP_ID: 1,
      IS_CREDITABLE: 'N',
      "0_0": '0',
      AMOUNT: 0
    };

    const defaultsChangeDetails = [returnObj];

    this.withholdTaxService.saveNonCreditableData(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      defaultsChangeDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('WithHold Tax Data has been successfully Deleted', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Remove from array
          this.defaultsMultipleSelected = this.defaultsMultipleSelected.filter((t: any) => t.TAG_KEY != selectedRowData.TAG_KEY);
          this.updateNonCreditableRecords(); // Update cached records
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error deleting row:', error);
        this._snackBar.open('Error deleting record. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  // Called when user cancels delete in modal
  onDeleteCancel() {
    this.showDeleteModal = false;
    this.deleteRowData = null;
    this.cdr.markForCheck();
  }

  // Matching AngularJS vm.saveCreditWHData
  saveCreditWHData() {
    // Disable the Save button till the processing completes
    this.isSaveCredClicked = true;
    this.crudLoading = true;
    this.cdr.markForCheck();

    const key = 0;
    let whTaxOnDividendAct = this.formData.whTaxOnDividend;
    if (whTaxOnDividendAct) {
      whTaxOnDividendAct = whTaxOnDividendAct.replace(/,/g, "");
    }

    const returnObj: any = {
      sso_id: this.appService.USER_SETTINGS?.user?.sso_id || this.logged_in_user,
      tax_year: this.formData.filterTaxYear,
      scenario: this.formData.scenario,
      object_id: this.defaultsMultipleSelected[key]?.COMBINATION_KEY || this.rowData.COMBINATION_KEY,
      combination_key: this.defaultsMultipleSelected[key]?.COMBINATION_KEY || this.rowData.COMBINATION_KEY,
      wht_dividend: (whTaxOnDividendAct !== '' && whTaxOnDividendAct !== null) ? whTaxOnDividendAct : null
    };

    const defaultsChangeDetails = [returnObj];

    this.withholdTaxService.saveCreditableData(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      defaultsChangeDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveCredClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Creditable Tax has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveCredClicked = false;
        this.crudLoading = false;
        console.error('Error saving creditable data:', error);
        this._snackBar.open('Error saving creditable data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  // Matching AngularJS vm.saveNonCreditWHData
  saveNonCreditWHData() {
    // Validations
    if (!this.formData.selectStdReasonCd || !this.formData.detailReason || !this.formData.applicableYearKey) {
      this._snackBar.open('Please select/enter all the fields.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (!this.formData.nonCrdAmount || this.formData.nonCrdAmount === '') {
      this._snackBar.open('Please enter a valid Withholding Tax Non-Creditable Amount.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.formData.nonCrdAmount != undefined && this.formData.nonCrdAmount != null) {
      const validNumberWithDecimals = ((parseInt('' + this.formData.nonCrdAmount) - parseFloat('' + this.formData.nonCrdAmount)) !== 0);
      if (validNumberWithDecimals) {
        this._snackBar.open('Please do not enter decimals for Withholding Tax Non-Creditable Amount.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
    }

    if (this.formData.selectStdReasonCd && 
        (this.formData.selectStdReasonCd == this.section909SplitrTaxKey || 
         this.formData.selectStdReasonCd == this.sectionReduction_GKEY || 
         this.formData.selectStdReasonCd == this.sectionReduction_AKEY)) {
      if (!this.formData.selectedSplitterBasketCatg) {
        this._snackBar.open('Please select Basket for selected Standard Reason Amount is Non-Creditable.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
      if (!this.formData.selectedSplitterCountryDesc) {
        this._snackBar.open('Please select Country for selected Standard Reason Amount is Non-Creditable.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
    }

    if (this.formData.selectStdReasonCd && 
        (this.formData.selectStdReasonCd == this.sectionReduction_GKEY || this.formData.selectStdReasonCd == this.sectionReduction_AKEY)) {
      if (!this.formData.selectedSplitterTradingPartnerCode) {
        this._snackBar.open('Please select Trading Partner for selected Standard Reason Amount is Non-Creditable.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
    }

    // Disable the Save button
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.markForCheck();

    const key = 0;
    const returnClobSettingsObj: any = {
      tax_year: this.formData.filterTaxYear,
      scenario: this.formData.scenario,
      object_id: this.defaultsMultipleSelected[key]?.COMBINATION_KEY || this.rowData.COMBINATION_KEY
    };

    const returnObj: any = {
      COMBINATION_KEY: this.defaultsMultipleSelected[key]?.COMBINATION_KEY || this.rowData.COMBINATION_KEY
    };

    // Build attribute keys
    const stdReasonAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'STANDARD_REASON_NOT_CREDITABLE']);
    const detailReasonAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'DETAIL_REASON_NOT_CREDITABLE']);
    const applicableYearAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'APPLICABLE_YEAR']);
    const basketAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'BASKET']);
    const countryAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'COUNTRY']);
    const actualTpKeyAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'ACTUAL_TP_KEY']);

    if (stdReasonAttrib) returnObj[stdReasonAttrib.ATTRIB_KEY + '_0'] = this.formData.selectStdReasonCd;
    if (detailReasonAttrib) returnObj[detailReasonAttrib.ATTRIB_KEY + '_0'] = this.formData.detailReason;
    if (applicableYearAttrib) returnObj[applicableYearAttrib.ATTRIB_KEY + '_0'] = this.formData.applicableYearKey;

    if (this.formData.selectStdReasonCd && 
        (this.formData.selectStdReasonCd == this.section909SplitrTaxKey || 
         this.formData.selectStdReasonCd == this.sectionReduction_GKEY || 
         this.formData.selectStdReasonCd == this.sectionReduction_AKEY)) {
      if (basketAttrib) returnObj[basketAttrib.ATTRIB_KEY + '_0'] = this.formData.selectedSplitterBasketCatg;
      if (countryAttrib) {
        if (this.selectedCountryCode && typeof this.selectedCountryCode === 'object') {
          returnObj[countryAttrib.ATTRIB_KEY + '_0'] = this.selectedCountryCode.CODE_KEY;
        } else if (this.selectedCountryCode) {
          returnObj[countryAttrib.ATTRIB_KEY + '_0'] = this.getCountryKey(this.selectedCountryCode);
        }
      }
      if (actualTpKeyAttrib) returnObj[actualTpKeyAttrib.ATTRIB_KEY + '_0'] = this.formData.selectedSplitterTradingPartnerCode;
      returnObj["IS_CREDITABLE"] = 'N';
    }

    returnObj["IS_CREDITABLE"] = 'N';
    returnObj["AMOUNT"] = this.formData.nonCrdAmount != null ? this.formData.nonCrdAmount : (this.defaultsMultipleSelected[key]?.NON_CREDIT_AMT || 0);
    returnObj['GROUP_ID'] = 1;

    if (this.formData.editFlagSet) {
      if (this.defaultsMultipleSelected[key]?.TAG_KEY != undefined && this.defaultsMultipleSelected[key]?.TAG_KEY != null) {
        returnObj["TAG_KEY"] = this.formData.tagKey != null ? this.formData.tagKey : this.defaultsMultipleSelected[key].TAG_KEY;
        returnObj["OPERATION_TYPE"] = 'U';
      } else {
        returnObj["TAG_KEY"] = null;
        returnObj["OPERATION_TYPE"] = 'I';
      }
    } else {
      returnObj["TAG_KEY"] = null;
      returnObj["OPERATION_TYPE"] = 'I';
    }

    const defaultsChangeDetails = [returnObj];

    this.withholdTaxService.saveNonCreditableData(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      defaultsChangeDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Non-Creditable Tax has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error saving non-creditable data:', error);
        this._snackBar.open('Error saving non-creditable data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  // Matching AngularJS vm.editNonCreditableTaxRow
  editNonCreditableTaxRow(selectedRowData: any) {
    // Set Standard Reason Code
    for (const reason of this.stdReasonCodeData) {
      if (reason.CODE_DISP_DESC == selectedRowData.STANDARD_REASON_NOT_CREDITABLE) {
        this.formData.selectStdReasonCd = '' + reason.CODE_KEY;
      }
    }
    
    // Reset applicable year based on selected reason
    this.isTaxPriorYear(); // Use immediate version for programmatic call
    
    // Set Applicable Year
    for (const taxYear of this.taxYearData) {
      if (taxYear.CODE_DISP_DESC == selectedRowData.APPLICABLE_YEAR) {
        this.formData.applicableYearKey = '' + taxYear.CODE_KEY;
      }
    }

    // Set Country
    if (selectedRowData.COUNTRY) {
      for (const country of this.countryData) {
        if (country.CODE_DISP_DESC == selectedRowData.COUNTRY) {
          this.formData.selectedSplitterCountryKey = '' + country.CODE_KEY;
          this.formData.selectedSplitterCountryDesc = country.CODE_DISP_DESC;
          this.selectedCountryCode = this.formData.selectedSplitterCountryDesc;
        }
      }
    } else {
      const exists = _.find(this.countryData, ['CODE_DISP_DESC', selectedRowData.LE_COUNTRY_NAME]);
      if (exists) {
        this.formData.selectedSplitterCountryKey = '' + exists.CODE_KEY;
        this.formData.selectedSplitterCountryDesc = exists.CODE_DISP_DESC;
        this.selectedCountryCode = this.formData.selectedSplitterCountryDesc;
      }
    }

    // Set Basket
    for (const basket of this.basketCatgData) {
      if (basket.CODE_DISP_DESC == selectedRowData.BASKET) {
        this.formData.selectedSplitterBasketCatg = '' + basket.CODE_KEY;
      }
    }

    // Set Trading Partner
    for (const tp of this.tradingPartnerCodeData) {
      if (tp.COMBINATION_KEY == selectedRowData.ACTUAL_TP_KEY) {
        this.selectedTradingPartnerCode = '' + tp.LE_NAME;
        this.formData.selectedSplitterTradingPartnerCode = '' + tp.COMBINATION_KEY;
      }
    }

    this.formData.editFlagSet = true;
    this.formData.nonCrdAmount = selectedRowData.NON_CREDIT_AMT;
    this.formData.detailReason = selectedRowData.DETAIL_REASON_NOT_CREDITABLE;
    this.formData.tagKey = selectedRowData.TAG_KEY;
    this.formData.whTaxOnDividend = selectedRowData.WHT_DIVIDEND ? this.numberWithCommas(parseFloat(selectedRowData.WHT_DIVIDEND)) : null;

    this.cdr.markForCheck();
  }

  // Matching AngularJS vm.reset
  reset() {
    this.tillCurrentTaxYear(this.formData.filterTaxYear, 0, 11);
    this.formData.detailReason = '';
    this.formData.nonCrdAmount = '';
    this.formData.applicableYearKey = this.getTaxYearKey(this.formData.filterTaxYear);
    this.formData.selectStdReasonCd = null;
    this.formData.selectedSplitterBasketCatg = this.basketCodeDefault.length > 0 ? '' + this.basketCodeDefault[0].CODE_KEY : '';
    this.formData.selectedSplitterCountryDesc = '';
    this.formData.selectedSplitterTradingPartnerCode = '';
    this.selectedTradingPartnerCode = null;
    this.selectedCountryCode = null;
    this.formData.tagKey = null;
    this.formData.editFlagSet = false;
    this.formData.isSection909SplitrTax = false;
    this.formData.isSectionReductionTax = false;
    this.cdr.markForCheck();
  }

  // Matching AngularJS vm.reset_credit
  reset_credit() {
    if (this.gridDataMultipleSelect?.rowData) {
      this.formData.whTaxOnDividend = this.gridDataMultipleSelect.rowData.WHT_DIVIDEND 
        ? this.numberWithCommas(parseFloat(this.gridDataMultipleSelect.rowData.WHT_DIVIDEND)) 
        : null;
    } else {
      this.formData.whTaxOnDividend = '';
    }
    this.cdr.markForCheck();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Update cached non-creditable records (call this whenever defaultsMultipleSelected changes)
  private updateNonCreditableRecords(): void {
    this.nonCreditableRecords = this.defaultsMultipleSelected.filter((t: any) => t.IS_CREDITABLE === 'N');
  }

  // TrackBy function for *ngFor performance optimization
  trackByTagKey(index: number, item: any): any {
    return item.TAG_KEY || index;
  }

  // TrackBy functions for dropdown lists - CRITICAL for performance with OnPush
  trackByCodeKey(index: number, item: any): any {
    return item?.CODE_KEY ?? index;
  }

  trackByCombinationKey(index: number, item: any): any {
    return item?.COMBINATION_KEY ?? index;
  }

  private refreshFormDropdowns(): void {
    this.stdReasonCodeOptionsView = mapToAppkitDropdownItems(this.stdReasonCodeOptions, 'label', 'value');
    this.formData.selectStdReasonCdItem = syncAppkitDropdownSelection(
      this.stdReasonCodeOptionsView,
      this.formData.selectStdReasonCd,
      'label',
      'value'
    );

    this.taxYearOptionsView = mapToAppkitDropdownItems(this.taxYearOptions, 'label', 'value');
    this.formData.applicableYearKeyItem = syncAppkitDropdownSelection(
      this.taxYearOptionsView,
      this.formData.applicableYearKey,
      'label',
      'value'
    );

    this.tradingPartnerOptionsView = mapToAppkitDropdownItems(this.tradingPartnerOptions, 'label', 'value');
    this.selectedTradingPartnerCodeItem = syncAppkitDropdownSelection(
      this.tradingPartnerOptionsView,
      this.selectedTradingPartnerCode,
      'label',
      'value'
    );

    this.basketOptionsView = mapToAppkitDropdownItems(this.basketOptions, 'label', 'value');
    this.formData.selectedSplitterBasketCatgItem = syncAppkitDropdownSelection(
      this.basketOptionsView,
      this.formData.selectedSplitterBasketCatg,
      'label',
      'value'
    );

    this.countryOptionsView = mapToAppkitDropdownItems(this.countryOptions, 'label', 'value');
    this.selectedCountryCodeItem = syncAppkitDropdownSelection(
      this.countryOptionsView,
      this.selectedCountryCode,
      'label',
      'value'
    );
  }

  onDropdownSelectItem(_event: any): void {}

  onIsTaxPriorYearFromDropdown(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.formData.selectStdReasonCdItem = selected;
      this.formData.selectStdReasonCd = selected.value;
      this.isTaxPriorYear();
    }
  }

  onFormDataApplicableYearKeySelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.formData.applicableYearKeyItem = selected;
      this.formData.applicableYearKey = selected.value;
    }
    this.cdr.markForCheck();
  }

  onChangeTradingPartnerFromDropdown(event: any): void {
    handleAppkitValueSelect(event, this, 'selectedTradingPartnerCodeItem', 'selectedTradingPartnerCode', () => {
      this.changeTradingPartner(this.selectedTradingPartnerCode);
    });
  }

  onFormDataSelectedSplitterBasketCatgSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.formData.selectedSplitterBasketCatgItem = selected;
      this.formData.selectedSplitterBasketCatg = selected.value;
    }
    this.cdr.markForCheck();
  }

  onChangeCountryFromDropdown(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedCountryCodeItem = selected;
      this.selectedCountryCode = selected.value;
      this.changeCountry(this.selectedCountryCode);
    }
  }
}
